import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  acceptQuote,
  deleteQuoteLine,
  fetchQuote,
  getActiveQuoteLines,
  normalizeQuoteLines,
  updateQuoteLines,
  updateSingleQuoteLine,
} from "../utils/shopApi";
import {
  STATUS_LABELS,
  STATUS_STYLES,
  didLineEditChangeToHistoryStatus,
  isHistoryQuote,
  isQuoteEditable,
  normalizeQuoteStatus,
  preserveQuoteStatusAfterLineEdit,
} from "../utils/quoteStatus";
import { notifyPendingQuotesChanged } from "../hooks/usePendingQuotes";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { NAV } from "../Navbar/navbarTheme";
import { AUTH_PRIMARY } from "./authStyles";

const PRIMARY = AUTH_PRIMARY;
const LINE = NAV.line;
const PAGE_BG = "#FFFFFF";
const STORAGE_BASE = "https://api.uktomars.co.uk/storage";

const qtyButtonSx = {
  borderRadius: 0,
  width: 36,
  height: 36,
  color: PRIMARY,
  "&:hover": { bgcolor: NAV.navyMuted },
};

function applyLineQuantityChange(lines, lineId, change) {
  const line = lines.find((entry) => entry.id === lineId);
  if (!line) return null;
  return (Number(line.quantity) || 0) + change;
}

function mergeQuoteAfterLineUpdate(beforeQuote, freshQuote) {
  return normalizeQuoteLines(
    preserveQuoteStatusAfterLineEdit(beforeQuote, freshQuote),
  );
}

function usesBatchQuantitySave(status) {
  const key = normalizeQuoteStatus(status);
  return key === "pending" || key === "in_review";
}

function linesQuantitySignature(lines) {
  return (Array.isArray(lines) ? lines : [])
    .map((line) => `${line.id}:${Number(line.quantity) || 0}`)
    .sort()
    .join("|");
}

const itemCardSx = {
  borderRadius: 3,
  border: `1px solid ${LINE}`,
  boxShadow: "0 12px 40px rgba(15, 40, 71, 0.08)",
  bgcolor: NAV.surface,
  overflow: "hidden",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 16px 48px rgba(15, 40, 71, 0.12)",
  },
};

const detailCellSx = {
  border: `1px solid ${LINE}`,
  borderRadius: 1.5,
  bgcolor: NAV.chipRowBg,
  py: 0.85,
  px: 1,
  minWidth: 0,
  textAlign: "center",
};

function disp(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function parsePrice(value) {
  if (value == null || value === "") return 0;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(amount) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function parseWeight(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function formatWeight(kg) {
  if (kg == null) return "—";
  return `${new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(kg)} kg`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function hasLinePrice(line) {
  return line?.unit_price != null && String(line.unit_price).trim() !== "";
}

function getLineSubtotal(line) {
  if (!hasLinePrice(line)) return null;
  const qty = Number(line?.quantity) || 0;
  const unit = parsePrice(line?.unit_price);
  return qty * unit;
}

function formatLineUnitPrice(line) {
  if (!hasLinePrice(line)) return "—";
  return formatGBP(parsePrice(line?.unit_price));
}

function formatLineSubtotal(line) {
  const subtotal = getLineSubtotal(line);
  return subtotal == null ? "—" : formatGBP(subtotal);
}

function getLineProductCode(line) {
  const product = line?.product;
  const code = product?.sku || product?.code;
  return code ? String(code).trim() : "—";
}

function getLineImage(line) {
  const product = line?.product;
  if (product?.image_url) return product.image_url;
  if (typeof product?.image === "string" && product.image.startsWith("http")) {
    return product.image;
  }
  if (typeof product?.image === "string" && product.image) {
    return `${STORAGE_BASE}/${product.image}`;
  }
  return "";
}

function getLineProductPath(line) {
  const productId = line?.product_id ?? line?.product?.id;
  return productId != null ? `/products/${productId}` : null;
}

function getLineTotalWeightKg(line) {
  const unitWeight = parseWeight(line?.product?.gross_weight);
  if (unitWeight == null) return null;
  const qty = Number(line?.quantity) || 0;
  return unitWeight * qty;
}

function getQuoteEstimatedWeight(lines) {
  const items = Array.isArray(lines) ? lines : [];
  let total = 0;
  let hasWeight = false;

  for (const line of items) {
    const lineWeight = getLineTotalWeightKg(line);
    if (lineWeight != null) {
      total += lineWeight;
      hasWeight = true;
    }
  }

  return hasWeight ? total : null;
}

function getQuotePricing(lines) {
  const items = Array.isArray(lines) ? lines : [];
  const pricedLines = items.filter(hasLinePrice);
  const total = pricedLines.reduce(
    (sum, line) => sum + (getLineSubtotal(line) ?? 0),
    0,
  );

  return {
    total,
    pricedCount: pricedLines.length,
    lineCount: items.length,
    hasUnpricedLines: pricedLines.length < items.length,
  };
}

const detailCell = (label, value) => (
  <Box sx={detailCellSx}>
    <Typography
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        color: NAV.inkMuted,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        mb: 0.35,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: PRIMARY,
        lineHeight: 1.3,
        wordBreak: "break-word",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const QuoteLineCard = ({
  line,
  quoteStatus,
  editable = false,
  lineUpdating = false,
  onDecrease,
  onIncrease,
  onRemove,
}) => {
  const imageUrl = getLineImage(line);
  const productPath = getLineProductPath(line);
  const product = line?.product;
  const caseSize = product?.case_size;
  const grossWeight = product?.gross_weight;
  const unitGrossWeight = parseWeight(grossWeight);
  const lineTotalWeight = getLineTotalWeightKg(line);
  const hasGrossWeight = unitGrossWeight != null;
  const hasPrice = hasLinePrice(line);
  const normalizedStatus = normalizeQuoteStatus(quoteStatus);
  const isPendingLine = normalizedStatus === "pending";

  return (
    <Card elevation={0} sx={itemCardSx}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
        >
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              border: `1px solid ${LINE}`,
              bgcolor: NAV.surfaceSubtle,
              flexShrink: 0,
              alignSelf: { xs: "center", sm: "flex-start" },
              width: { xs: 140, sm: 108 },
              height: { xs: 140, sm: 108 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={line.product_name || "Product"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <Inventory2OutlinedIcon
                sx={{ color: NAV.inkMuted, fontSize: 40 }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={1}
              sx={{ mb: 1.25 }}
            >
              {productPath ? (
                <Typography
                  component={Link}
                  to={productPath}
                  state={{ product }}
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1rem", sm: "1.0625rem" },
                    color: PRIMARY,
                    lineHeight: 1.35,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {line.product_name || "Unnamed product"}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1rem", sm: "1.0625rem" },
                    color: PRIMARY,
                    lineHeight: 1.35,
                  }}
                >
                  {line.product_name || "Unnamed product"}
                </Typography>
              )}
              {!isPendingLine && hasPrice ? (
                <Chip
                  label={formatLineUnitPrice(line)}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: NAV.navyMuted,
                    color: PRIMARY,
                    flexShrink: 0,
                    borderRadius: 1.5,
                  }}
                />
              ) : null}
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 0.75,
                mb: !isPendingLine ? 0.75 : 2,
              }}
            >
              {detailCell("Product Code", getLineProductCode(line))}
              {detailCell("Case size", disp(caseSize))}
              {detailCell("Quantity", disp(line.quantity))}
              {detailCell(
                "Gross Weight",
                hasGrossWeight ? formatWeight(unitGrossWeight) : "—",
              )}
            </Box>

            <Box
              sx={{
                pt: 1.5,
                borderTop: `1px solid ${LINE}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {editable ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      border: `1px solid ${LINE}`,
                      borderRadius: 999,
                      bgcolor: NAV.surfaceSubtle,
                      overflow: "hidden",
                    }}
                  >
                    <IconButton
                      type="button"
                      size="small"
                      aria-label="Decrease quantity"
                      disabled={lineUpdating}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onDecrease?.();
                      }}
                      sx={qtyButtonSx}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        minWidth: 32,
                        textAlign: "center",
                        color: PRIMARY,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {line.quantity}
                    </Typography>
                    <IconButton
                      type="button"
                      size="small"
                      aria-label="Increase quantity"
                      disabled={lineUpdating}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onIncrease?.();
                      }}
                      sx={qtyButtonSx}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Remove item"
                    disabled={lineUpdating}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onRemove?.();
                    }}
                    sx={{
                      color: NAV.inkMuted,
                      border: `1px solid ${LINE}`,
                      borderRadius: 999,
                      "&:hover": {
                        color: "#e31837",
                        bgcolor: "rgba(227, 24, 55, 0.06)",
                        borderColor: "rgba(227, 24, 55, 0.25)",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : null}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: NAV.navyMuted,
                  width: "fit-content",
                  ml: editable ? "auto" : "auto",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: PRIMARY,
                    fontSize: "1rem",
                  }}
                >
                  {isPendingLine
                    ? `Total weight: ${formatWeight(lineTotalWeight)}`
                    : `Total price: ${formatLineSubtotal(line)}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const QuoteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state) => state.auth);

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingLineId, setUpdatingLineId] = useState(null);
  const [draftLines, setDraftLines] = useState(null);
  const [savingQuantities, setSavingQuantities] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [serverStatusMismatch, setServerStatusMismatch] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    setAgreedToTerms(false);
    setAccepting(false);
    setServerStatusMismatch(false);
    setDraftLines(null);
  }, [id]);

  useEffect(() => {
    if (!token) {
      navigate("/SignIn", { replace: true });
      return undefined;
    }

    let cancelled = false;

    const loadQuote = async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await fetchQuote(id);
        if (!cancelled) {
          setQuote(payload);
          setServerStatusMismatch(false);
        }
      } catch (loadError) {
        if (cancelled) return;
        if (loadError?.code === "AUTH_REQUIRED") {
          navigate("/SignIn", { replace: true });
          return;
        }
        setError(
          loadError?.message ||
            "Unable to load quote details right now. Please try again later.",
        );
        setQuote(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadQuote();

    return () => {
      cancelled = true;
    };
  }, [id, token, navigate]);

  const savedLines = useMemo(() => getActiveQuoteLines(quote?.lines), [quote]);
  const batchQuantitySave =
    quote != null && usesBatchQuantitySave(quote.status);
  const lines = useMemo(() => {
    if (batchQuantitySave && draftLines != null) {
      return draftLines;
    }
    return savedLines;
  }, [batchQuantitySave, draftLines, savedLines]);
  const hasUnsavedQuantityChanges = useMemo(() => {
    if (!batchQuantitySave || draftLines == null) return false;
    return (
      linesQuantitySignature(draftLines) !== linesQuantitySignature(savedLines)
    );
  }, [batchQuantitySave, draftLines, savedLines]);

  useEffect(() => {
    if (!batchQuantitySave) {
      setDraftLines(null);
      return;
    }
    setDraftLines((current) =>
      current == null ? savedLines.map((line) => ({ ...line })) : current,
    );
  }, [batchQuantitySave, savedLines]);
  const lineCount = useMemo(
    () => lines.reduce((sum, line) => sum + (Number(line?.quantity) || 0), 0),
    [lines],
  );
  const pricing = useMemo(() => getQuotePricing(lines), [lines]);
  const estimatedTotalWeight = useMemo(
    () => getQuoteEstimatedWeight(lines),
    [lines],
  );

  const statusKey = normalizeQuoteStatus(quote?.status);
  const isPending = statusKey === "pending";
  const isQuoted = statusKey === "quoted";
  const statusLabel = STATUS_LABELS[statusKey] ?? quote?.status ?? "Unknown";
  const statusStyle = STATUS_STYLES[statusKey] ?? STATUS_STYLES.pending;
  const listPath =
    location.state?.listPath ??
    (quote && isHistoryQuote(quote)
      ? "/QuoteHistory/history"
      : "/QuoteHistory");
  const backLabel =
    listPath === "/QuoteHistory/history"
      ? "Back to order history"
      : "Back to requested quotes";
  const isEditable = isQuoteEditable(quote);

  const applyLineEditResult = (beforeQuote, freshQuote) => {
    const mismatch = didLineEditChangeToHistoryStatus(beforeQuote, freshQuote);
    setServerStatusMismatch(mismatch);
    if (!mismatch) {
      setError("");
    }
    return mergeQuoteAfterLineUpdate(beforeQuote, freshQuote);
  };

  const persistQuoteLineChange = async (lineId, nextQuantity) => {
    if (!quote || !isQuoteEditable(quote)) {
      setError("This quote can no longer be edited.");
      return;
    }

    const quoteBeforeEdit = quote;
    setUpdatingLineId(lineId);
    setError("");
    try {
      if (nextQuantity < 1) {
        setQuote((current) =>
          normalizeQuoteLines({
            ...current,
            lines: (current?.lines || []).filter((line) => line.id !== lineId),
          }),
        );
        await deleteQuoteLine(quoteBeforeEdit.id, lineId);
      } else {
        setQuote((current) =>
          normalizeQuoteLines({
            ...current,
            lines: (current?.lines || []).map((entry) =>
              entry.id === lineId
                ? { ...entry, quantity: nextQuantity }
                : entry,
            ),
          }),
        );
        const updated = await updateSingleQuoteLine(
          quoteBeforeEdit,
          lineId,
          nextQuantity,
        );
        const freshQuote = updated?.lines
          ? updated
          : await fetchQuote(quoteBeforeEdit.id);
        setQuote(applyLineEditResult(quoteBeforeEdit, freshQuote));
        notifyPendingQuotesChanged();
        return;
      }

      const freshQuote = await fetchQuote(quoteBeforeEdit.id);
      setQuote(applyLineEditResult(quoteBeforeEdit, freshQuote));
      notifyPendingQuotesChanged();
    } catch (updateError) {
      if (updateError?.code === "AUTH_REQUIRED") {
        navigate("/SignIn", { replace: true });
        return;
      }
      setError(
        updateError?.message || "Unable to update this quote right now.",
      );
      const freshQuote = await fetchQuote(quoteBeforeEdit.id);
      setQuote(applyLineEditResult(quoteBeforeEdit, freshQuote));
    } finally {
      setUpdatingLineId(null);
    }
  };

  const applyDraftLineQuantityChange = (lineId, change) => {
    setDraftLines((current) => {
      const baseLines = current ?? savedLines.map((line) => ({ ...line }));
      const nextQuantity = applyLineQuantityChange(baseLines, lineId, change);
      if (nextQuantity == null) return baseLines;
      if (nextQuantity < 1) {
        return baseLines.filter((line) => line.id !== lineId);
      }
      return baseLines.map((line) =>
        line.id === lineId ? { ...line, quantity: nextQuantity } : line,
      );
    });
  };

  const applyDraftLineRemove = (lineId) => {
    setDraftLines((current) => {
      const baseLines = current ?? savedLines.map((line) => ({ ...line }));
      return baseLines.filter((line) => line.id !== lineId);
    });
  };

  const handleLineQuantityChange = async (lineId, change) => {
    if (batchQuantitySave) {
      applyDraftLineQuantityChange(lineId, change);
      return;
    }

    const nextQuantity = applyLineQuantityChange(savedLines, lineId, change);
    if (nextQuantity == null) return;
    await persistQuoteLineChange(lineId, nextQuantity);
  };

  const handleLineRemove = async (lineId) => {
    if (!quote || !isQuoteEditable(quote)) {
      setError("This quote can no longer be edited.");
      return;
    }

    if (batchQuantitySave) {
      applyDraftLineRemove(lineId);
      return;
    }

    const quoteBeforeEdit = quote;
    setUpdatingLineId(lineId);
    setError("");
    try {
      setQuote((current) =>
        normalizeQuoteLines({
          ...current,
          lines: (current?.lines || []).filter((line) => line.id !== lineId),
        }),
      );
      await deleteQuoteLine(quoteBeforeEdit.id, lineId);
      const freshQuote = await fetchQuote(quoteBeforeEdit.id);
      setQuote(applyLineEditResult(quoteBeforeEdit, freshQuote));
      notifyPendingQuotesChanged();
    } catch (updateError) {
      if (updateError?.code === "AUTH_REQUIRED") {
        navigate("/SignIn", { replace: true });
        return;
      }
      setError(
        updateError?.message || "Unable to remove this quote line right now.",
      );
      const freshQuote = await fetchQuote(quoteBeforeEdit.id);
      setQuote(applyLineEditResult(quoteBeforeEdit, freshQuote));
    } finally {
      setUpdatingLineId(null);
    }
  };

  const handleSaveQuantities = async () => {
    if (
      !quote ||
      !batchQuantitySave ||
      !draftLines ||
      !hasUnsavedQuantityChanges ||
      savingQuantities
    ) {
      return;
    }

    const quoteBeforeEdit = quote;
    setSavingQuantities(true);
    setError("");

    try {
      const draftIds = new Set(draftLines.map((line) => line.id));
      const removedLines = savedLines
        .filter((line) => !draftIds.has(line.id))
        .map((line) => ({ id: line.id, quantity: 0 }));
      const updatedLines = draftLines.map((line) => ({
        id: line.id,
        quantity: Number(line.quantity) || 0,
      }));

      const updated = await updateQuoteLines(
        quoteBeforeEdit.id,
        [...updatedLines, ...removedLines],
        { allowZeroQuantity: removedLines.length > 0 },
      );
      const freshQuote = updated?.lines
        ? updated
        : await fetchQuote(quoteBeforeEdit.id);
      const mergedQuote = applyLineEditResult(quoteBeforeEdit, freshQuote);
      setQuote(mergedQuote);
      setDraftLines(
        getActiveQuoteLines(mergedQuote.lines).map((line) => ({ ...line })),
      );
      notifyPendingQuotesChanged();
    } catch (saveError) {
      if (saveError?.code === "AUTH_REQUIRED") {
        navigate("/SignIn", { replace: true });
        return;
      }
      setError(
        saveError?.message || "Unable to save quote quantities right now.",
      );
    } finally {
      setSavingQuantities(false);
    }
  };

  const handleAcceptQuote = async (event) => {
    if (event?.type !== "click") return;

    event.preventDefault();
    event.stopPropagation();

    if (
      !quote ||
      !isQuoted ||
      !agreedToTerms ||
      accepting ||
      updatingLineId != null ||
      savingQuantities
    ) {
      return;
    }

    setAccepting(true);
    setError("");

    try {
      const freshBeforeAccept = await fetchQuote(quote.id);
      const serverStatus = normalizeQuoteStatus(freshBeforeAccept.status);

      if (serverStatus === "accepted" || serverStatus === "ordered") {
        setQuote(freshBeforeAccept);
        setAgreedToTerms(false);
        setServerStatusMismatch(true);
        navigate(location.pathname, {
          replace: true,
          state: { listPath: "/QuoteHistory/history" },
        });
        notifyPendingQuotesChanged();
        return;
      }

      if (serverStatus !== "quoted") {
        setQuote(freshBeforeAccept);
        setAgreedToTerms(false);
        setError(
          `This quote can only be accepted when status is quoted. Current status: ${serverStatus}.`,
        );
        notifyPendingQuotesChanged();
        return;
      }

      const accepted = await acceptQuote(quote.id);
      const freshQuote = accepted?.id ? accepted : await fetchQuote(quote.id);
      setQuote(freshQuote);
      setAgreedToTerms(false);
      setServerStatusMismatch(false);
      navigate(location.pathname, {
        replace: true,
        state: { listPath: "/QuoteHistory/history" },
      });
      notifyPendingQuotesChanged();
    } catch (updateError) {
      if (updateError?.code === "AUTH_REQUIRED") {
        navigate("/SignIn", { replace: true });
        return;
      }
      const message =
        updateError?.message || "Unable to accept this quote right now.";
      setError(message);

      if (
        String(message)
          .toLowerCase()
          .includes("only be accepted when status is quoted")
      ) {
        try {
          const freshQuote = await fetchQuote(quote.id);
          setQuote(freshQuote);
          setAgreedToTerms(false);
          setServerStatusMismatch(
            normalizeQuoteStatus(freshQuote.status) === "accepted" ||
              normalizeQuoteStatus(freshQuote.status) === "ordered",
          );
          notifyPendingQuotesChanged();
        } catch {
          // keep current quote if refresh fails
        }
      }
    } finally {
      setAccepting(false);
    }
  };

  if (!token) {
    return null;
  }

  if (loading && !quote) {
    return (
      <Box
        sx={{
          pt: "clamp(104px, 14vh, 150px)",
          pb: 6,
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: PAGE_BG,
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pt: "clamp(104px, 14vh, 150px)",
        pb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
        maxWidth: 1320,
        mx: "auto",
        minHeight: "70vh",
        background: PAGE_BG,
      }}
    >
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #163a5e 55%, #1a4570 100%)`,
          boxShadow: "0 18px 48px rgba(15, 40, 71, 0.18)",
          color: "#fff",
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: { xs: 52, sm: 60 },
              height: { xs: 52, sm: 60 },
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.14)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
              flexShrink: 0,
            }}
          >
            <ReceiptLongOutlinedIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.02em",
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                lineHeight: 1.15,
              }}
            >
              {quote?.reference || `Quote #${quote?.id ?? id}`}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: "0.9375rem",
                opacity: 0.9,
                maxWidth: 520,
                lineHeight: 1.5,
              }}
            >
              Submitted {formatDate(quote?.created_at)}
              {quote?.updated_at && quote.updated_at !== quote.created_at
                ? ` · Updated ${formatDate(quote.updated_at)}`
                : ""}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            label={statusLabel}
            sx={{
              fontWeight: 700,
              height: 36,
              ...statusStyle.chip,
            }}
          />
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {serverStatusMismatch ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          This quote&apos;s status has changed and is now in your order history.
        </Alert>
      ) : null}

      {isEditable ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {batchQuantitySave
            ? "Adjust product quantities as needed. A Save quantity button will appear when you have unsaved changes."
            : `Quantities can be updated until you accept this quote.`}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          alignItems: "flex-start",
        }}
      >
        <Stack spacing={2}>
          {lines.length === 0 ? (
            <Card elevation={0} sx={itemCardSx}>
              <CardContent sx={{ py: 5, px: 3, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No line items on this quote request.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            lines.map((line) => (
              <QuoteLineCard
                key={line.id ?? `${line.product_slug}-${line.quantity}`}
                line={line}
                quoteStatus={quote?.status}
                editable={isEditable}
                lineUpdating={
                  savingQuantities ||
                  (!batchQuantitySave && updatingLineId === line.id)
                }
                onDecrease={() => handleLineQuantityChange(line.id, -1)}
                onIncrease={() => handleLineQuantityChange(line.id, 1)}
                onRemove={() => handleLineRemove(line.id)}
              />
            ))
          )}
        </Stack>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${LINE}`,
            boxShadow: "0 16px 48px rgba(15, 40, 71, 0.1)",
            bgcolor: NAV.surface,
            overflow: "hidden",
            height: "fit-content",
            position: { lg: "sticky" },
            top: { lg: 120 },
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              background: `linear-gradient(135deg, ${PRIMARY} 0%, #163a5e 100%)`,
              color: "#fff",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, letterSpacing: "0.02em" }}
            >
              Quote summary
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", opacity: 0.88, mt: 0.35 }}>
              Request overview
            </Typography>
          </Box>
          <CardContent sx={{ p: 2.5 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Line items</Typography>
                <Typography fontWeight={700} sx={{ color: PRIMARY }}>
                  {lines.length}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Total cases</Typography>
                <Typography fontWeight={700} sx={{ color: PRIMARY }}>
                  {lineCount}
                </Typography>
              </Stack>
              {!isPending && estimatedTotalWeight != null ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Estimated gross weight
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: PRIMARY }}>
                    {formatWeight(estimatedTotalWeight)}
                  </Typography>
                </Stack>
              ) : null}

              <Divider sx={{ my: 0.5 }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  py: 1,
                  px: 1.25,
                  borderRadius: 2,
                  bgcolor: NAV.chipRowBg,
                  border: `1px solid ${LINE}`,
                }}
              >
                <Typography sx={{ fontWeight: 800, color: PRIMARY }}>
                  {isPending ? "Estimated total weight" : "Grand total"}
                </Typography>
                <Typography
                  sx={{ fontWeight: 800, color: PRIMARY, fontSize: "1.2rem" }}
                >
                  {isPending
                    ? formatWeight(estimatedTotalWeight)
                    : pricing.pricedCount > 0
                      ? pricing.hasUnpricedLines
                        ? `${formatGBP(pricing.total)}+`
                        : formatGBP(pricing.total)
                      : "—"}
                </Typography>
              </Stack>
            </Stack>

            {quote?.customer_note ? (
              <Box sx={{ mt: 2.25 }}>
                <Typography sx={{ fontWeight: 700, color: PRIMARY, mb: 0.75 }}>
                  Note
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: NAV.surfaceSubtle,
                    border: `1px solid ${LINE}`,
                  }}
                >
                  <NotesOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: NAV.inkMuted,
                      mt: 0.15,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: NAV.inkMuted,
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {quote.customer_note}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {batchQuantitySave && hasUnsavedQuantityChanges ? (
              <Box sx={{ mt: 2.5 }}>
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  disabled={savingQuantities || lines.length === 0}
                  onClick={handleSaveQuantities}
                  startIcon={
                    savingQuantities ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  sx={{
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: PRIMARY,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#141a33",
                      boxShadow: "none",
                    },
                  }}
                >
                  {savingQuantities ? "Saving..." : "Save quantity"}
                </Button>
              </Box>
            ) : null}

            {isQuoted ? (
              <Box sx={{ mt: 2.5 }}>
                <FormControlLabel
                  sx={{
                    alignItems: "flex-start",
                    mx: 0,
                    mb: 1.5,
                  }}
                  control={
                    <Checkbox
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      disabled={
                        accepting || updatingLineId != null || savingQuantities
                      }
                      sx={{
                        pt: 0.25,
                        color: PRIMARY,
                        "&.Mui-checked": { color: PRIMARY },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        color: "text.secondary",
                        lineHeight: 1.5,
                        mt: 0.15,
                      }}
                    >
                      I have read and agree to the{" "}
                      <Box
                        component={Link}
                        to="/TermsAndConditions"
                        sx={{
                          color: PRIMARY,
                          fontWeight: 700,
                          textDecoration: "underline",
                          "&:hover": { color: "#141a33" },
                        }}
                      >
                        Terms and Conditions
                      </Box>{" "}
                      and the{" "}
                      <Box
                        component={Link}
                        to="/PrivacyPolicy"
                        sx={{
                          color: PRIMARY,
                          fontWeight: 700,
                          textDecoration: "underline",
                          "&:hover": { color: "#141a33" },
                        }}
                      >
                        Privacy Policy
                      </Box>
                      .
                    </Typography>
                  }
                />
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  disabled={
                    accepting ||
                    !agreedToTerms ||
                    updatingLineId != null ||
                    savingQuantities
                  }
                  onClick={handleAcceptQuote}
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: "#15803d",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#166534",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#e5e7eb",
                      color: "#9ca3af",
                      opacity: 1,
                    },
                  }}
                >
                  {accepting ? "Accepting..." : "Accept quote & Place order"}
                </Button>
              </Box>
            ) : null}

            <Button
              component={Link}
              to={listPath}
              fullWidth
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{
                mt: 2.5,
                py: 1.2,
                textTransform: "none",
                fontWeight: 700,
                color: PRIMARY,
                borderColor: PRIMARY,
                borderRadius: 2,
                "&:hover": {
                  borderColor: PRIMARY,
                  bgcolor: "rgba(29, 36, 69, 0.06)",
                },
              }}
            >
              {backLabel}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default QuoteDetailPage;
